import React from "react";
import { Calendar, Pill, FileText, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { PrescriptionViewerModal } from "./PrescriptionViewerModal";
import { DashboardPrescriptionModal } from "./DashboardPrescriptionModal";
import { ReferralViewerModal } from "./ReferralViewerModal";
import { Prescription } from "@/lib/dashboard/prescriptionsData";
import { Referral } from "@/lib/dashboard/referralsData";
import { Consultation } from "@/lib/dashboard/consultationsData";
import {
  PatientInfo,
  usePatientFormState,
} from "@/lib/dashboard/patientDashboardData";
import ConsultationDetails from "./ConsultationDetails";

interface PatientModalsProps {
  // Prescription Modal States
  prescriptionViewerOpen: boolean;
  setPrescriptionViewerOpen: (open: boolean) => void;
  prescriptionEditOpen: boolean;
  setPrescriptionEditOpen: (open: boolean) => void;
  selectedPrescription: Prescription | null;
  setSelectedPrescription: (prescription: Prescription | null) => void;

  // Referral Modal States
  referralViewerOpen: boolean;
  setReferralViewerOpen: (open: boolean) => void;
  referralEditOpen: boolean;
  setReferralEditOpen: (open: boolean) => void;
  selectedReferral: Referral | null;
  setSelectedReferral: (referral: Referral | null) => void;

  // Consultation Modal States
  selectedConsultation: Consultation | null;
  setSelectedConsultation: (consultation: Consultation | null) => void;

  // Handlers
  onEditFromViewer: () => void;
  onSavePrescription: (prescriptionData: any) => void;
  onEditReferralFromViewer: () => void;
  onSaveReferral: (referralData: any) => void;

  // Other props
  patient: any;
  patientData: PatientInfo;
  isDarkMode: boolean;
  prescriptionImagePreview: string;
}

export function DashboardPatientModals({
  prescriptionViewerOpen,
  setPrescriptionViewerOpen,
  prescriptionEditOpen,
  setPrescriptionEditOpen,
  selectedPrescription,
  setSelectedPrescription,
  referralViewerOpen,
  setReferralViewerOpen,
  referralEditOpen,
  setReferralEditOpen,
  selectedReferral,
  setSelectedReferral,
  selectedConsultation,
  setSelectedConsultation,
  onEditFromViewer,
  onSavePrescription,
  onEditReferralFromViewer,
  onSaveReferral,
  patient,
  patientData,
  isDarkMode,
  prescriptionImagePreview,
}: PatientModalsProps) {
  return (
    <>
      {/* Prescription Viewer Modal */}
      <PrescriptionViewerModal
        isOpen={prescriptionViewerOpen}
        onOpenChange={setPrescriptionViewerOpen}
        prescription={selectedPrescription}
        onEdit={onEditFromViewer}
        isDarkMode={isDarkMode}
        prescriptionImagePreview={prescriptionImagePreview}
      />

      {/* Prescription Edit Modal */}
      <DashboardPrescriptionModal
        isOpen={prescriptionEditOpen}
        onOpenChange={setPrescriptionEditOpen}
        editMode={true}
        patient={patient}
        existingPrescription={
          selectedPrescription
            ? {
                id: selectedPrescription.id.toString(),
                patientName: patient?.patientName,
                patientInitials: (patient?.patientName)
                  .split(" ")
                  .map((n) => n[0])
                  .join(""),
                patientNHS: patient?.nhsNumber,
                medication: selectedPrescription.medication,
                dosage: "1 tablet", // Default value - in real app this would come from prescription data
                quantity: "30", // Default value - in real app this would come from prescription data
                duration: "30 days", // Default value - in real app this would come from prescription data
                indication: "As prescribed", // Default value - in real app this would come from prescription data
                priority: "routine" as const,
                prescribedBy: "Dr. Smith", // Default value - in real app this would come from prescription data
              }
            : undefined
        }
        onSave={onSavePrescription}
        isDarkMode={isDarkMode}
      />

      {/* Referral Viewer Modal */}
      <ReferralViewerModal
        isOpen={referralViewerOpen}
        onOpenChange={setReferralViewerOpen}
        referral={selectedReferral}
        onEdit={onEditReferralFromViewer}
        isDarkMode={isDarkMode}
      />

      {referralEditOpen && selectedReferral && (
        <Dialog open={referralEditOpen} onOpenChange={setReferralEditOpen}>
          <DialogContent className="max-w-6xl h-[90vh] overflow-hidden p-0 flex flex-col">
            <DialogHeader className="flex-shrink-0 p-6 pb-4 border-b border-gray-200">
              <DialogTitle className="text-xl font-semibold">
                Edit Referral Letter - {selectedReferral.type}
              </DialogTitle>
            </DialogHeader>
            <div className="flex-1 p-6">
              <div className="text-center">
                <p className="mb-4 text-gray-600">
                  Editing referral letter {selectedReferral.number}
                </p>
                <p className="text-sm text-gray-500">
                  In a full implementation, this would open the referral editor
                  with the current letter content.
                </p>
                <div className="flex justify-center gap-3 mt-6">
                  <Button
                    variant="outline"
                    onClick={() => setReferralEditOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => {
                      onSaveReferral({
                        id: selectedReferral.id,
                        type: selectedReferral.type,
                        content: "Updated referral content",
                        timestamp: new Date(),
                      });
                    }}
                  >
                    Save Changes
                  </Button>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Consultation Detail Modal */}
      <ConsultationDetails />
    </>
  );
}
